import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { ProjectDocumentView } from 'app/edozvola/cases/project-documents/model/project-documents.model';
import { convertDateFromServer } from 'app/shared/util/date-utils';
import ViewTableHeaderColumn from 'app/shared/view-table/view-table-header-column';
import ViewTable from 'app/shared/view-table/view-table';
import {
  copyProjectDocumentQuery,
  deleteProjectDocumentQuery,
  fetchProjectDocumentsQuery,
} from 'app/edozvola/cases/project-documents/service/project-document.queries';
import { Button } from 'edozvola-frontend-lib/dist/ui/components';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { AttachmentModalVariant } from 'app/edozvola/cases/project-documents/attachment-modal/attachment-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TABLE_QUERY_KEYS } from 'app/config/constants';
import { getInvalidRowsForTable } from 'app/shared/util/validation-errors.util';
import { getCaseContext } from 'app/shared/util/context-util';
import { TooltipWrapper } from 'edozvola-frontend-lib/dist/ui/components/tooltipwrapper';

const ProjectDocumentsTable = ({ setProjectDocument, setReadOnly, setProjectDocumentForAttachments, setProjectAttachmentVariant }) => {
  const { id: uuid } = useParams<{ id: string }>();
  const [idProjectDocumentToDelete, setIdProjectDocumentToDelete] = useState();
  const [deleteModalShown, setDeleteModalShown] = useState(false);
  const { sidebarItemPair } = getCaseContext();

  const invalidRowIds = getInvalidRowsForTable(TABLE_QUERY_KEYS.PROJECT_DOCUMENTS_TABLE);

  const { mutate: deleteProjectDocumentMutation, isPending: isDeletingProjectDocument } = deleteProjectDocumentQuery();
  const { mutate: copyProjectDocumentMutation, isPending: isCopyingProjectDocument } = copyProjectDocumentQuery();

  const openProjectDocumentForm = (idProjectDocument: number, readOnly: boolean) => {
    setProjectDocument(idProjectDocument);
    setReadOnly(readOnly);
  };
  const openDeleteDialog = idToDelete => {
    setIdProjectDocumentToDelete(idToDelete);
    setDeleteModalShown(true);
  };

  const removeProjectDocument = () => {
    setDeleteModalShown(false);
    deleteProjectDocumentMutation(idProjectDocumentToDelete);
  };

  const openAttachmentModal = (value: ProjectDocumentView, type: AttachmentModalVariant) => {
    setProjectDocumentForAttachments(value.idProjectDocument);
    setProjectAttachmentVariant(type);
  };

  const renderTableRow = (value: ProjectDocumentView, projectDocumentIndex: number, totalItems: number) => {
    return (
      <tr key={projectDocumentIndex} className={invalidRowIds.includes(value.idProjectDocument) ? 'invalid-table-row' : ''}>
        <td>
          <div className={'column-with-multiple-data'}>
            <span>{value.projectType}</span>
            <span>{value.additionalDescription}</span>
          </div>
        </td>
        <td>
          <div className={'column-with-multiple-data'}>
            <span>{value.authorizedDesigner}</span>
            <span>{value.designerOfficeAddress}</span>
          </div>
        </td>
        <td>{value.projectMap}</td>
        <td>
          <div className={'column-with-multiple-data'}>
            <span>{value.projectMark}</span>
            <span>{convertDateFromServer(value.projectCreatedDate)}</span>
          </div>
        </td>

        <td>
          <div className={'actions-container'}>
            {sidebarItemPair?.canAddAttachment && (
              <TooltipWrapper message={'Dodaj prilog'}>
                <FontAwesomeIcon icon={'plus'} className={'icon-table'} onClick={() => openAttachmentModal(value, 'ADD')} />
              </TooltipWrapper>
            )}
            {sidebarItemPair?.canViewAttachments && value.hasAttachments && (
              <TooltipWrapper message={'Pregledaj priloge'}>
                <FontAwesomeIcon icon={'eye'} className={'icon-table'} onClick={() => openAttachmentModal(value, 'VIEW')} />
              </TooltipWrapper>
            )}
          </div>
        </td>
        <td>
          <div className={'actions-container'}>
            {sidebarItemPair?.canView && (
              <TooltipWrapper message={'Pregledaj'}>
                <FontAwesomeIcon
                  icon={'eye'}
                  className={'icon-table'}
                  onClick={() => openProjectDocumentForm(value.idProjectDocument, true)}
                />
              </TooltipWrapper>
            )}
            {sidebarItemPair?.canEdit && value.canEdit && (
              <TooltipWrapper message={'Uredi'}>
                <FontAwesomeIcon
                  icon={'pen-to-square'}
                  className={'icon-table'}
                  onClick={() => openProjectDocumentForm(value.idProjectDocument, false)}
                />
              </TooltipWrapper>
            )}
            {sidebarItemPair?.canCopy && (
              <TooltipWrapper message={'Kopiraj'}>
                <FontAwesomeIcon
                  icon={'copy'}
                  className={'icon-table'}
                  onClick={() => copyProjectDocumentMutation(value.idProjectDocument)}
                />
              </TooltipWrapper>
            )}
            {sidebarItemPair?.canDelete && value.canDelete && (
              <TooltipWrapper message={'Obriši'}>
                <FontAwesomeIcon icon={'trash-alt'} className={'icon-table'} onClick={() => openDeleteDialog(value.idProjectDocument)} />
              </TooltipWrapper>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <>
      <ViewTable
        renderTableRow={renderTableRow}
        noDataMessage={'Nema projekata'}
        initialPaginationColumn={'ordering'}
        fetchDataQuery={fetchProjectDocumentsQuery}
        fetchDataProps={[uuid]}
      >
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Vrsta projekta', 'Dodatni opis']} width={'17%'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Ovlašteni projektant']} width={'45%'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Mapa']} width={'10%'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Oznaka', 'Datum izrade']} width={'8%'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Prilozi']} width={'5%'} />
        <ViewTableHeaderColumn renderSort={false} columnLabels={['Akcije']} />
      </ViewTable>
      <Modal scrollable isOpen={deleteModalShown} size={'lg'} toggle={() => setDeleteModalShown(false)} centered>
        <ModalHeader>
          <span>Brisanje projekta</span>
        </ModalHeader>
        <ModalBody>
          <div>Jeste li sigurni da želite obrisati podatke?</div>
        </ModalBody>
        <ModalFooter>
          <Button size={'lg'} label="Odustani" outline onClick={() => setDeleteModalShown(false)} color={'secondary'} />
          <Button size={'lg'} label="Obriši" onClick={removeProjectDocument} color={'danger'} />
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ProjectDocumentsTable;
